"use client";

import { DetailSkeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/page-header";
import { SaveButton, type SaveStatus } from "@/components/ui/save-button";
import { UserAgentsSection } from "@/components/user-agents-section";
import { AccountAccessDialog } from "@/components/account-access-dialog";
import { useSession } from "@/hooks/use-session";
import { PanelLayout } from "@/components/ui/panel-layout";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { DbModelTier } from "@/types/db";
import { BadgeCheckIcon, ChevronRightIcon, CircleXIcon, PanelLeftClose, PanelLeftOpen, PlusIcon, TrashIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

// ─── Types ─────────────────────────────────────────────────────────────────────

type UserEditRole = "user" | "admin" | "superadmin";
type TeamMembershipRole = "user" | "admin";

const TIER_OPTIONS: Array<{ value: DbModelTier; label: string }> = [
  { value: "standard", label: "Standard" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
  { value: "experimental", label: "Experimental" },
];

type TeamItem = {
  id: string;
  name: string;
  is_home: boolean;
  relationship: "owner" | "member";
  role: string;
  joined_at: string;
};

type UserDetail = {
  profile: {
    user_id: string;
    display_name: string | null;
    username: string | null;
    role: string | null;
    team: string | null;
    bio: string | null;
    model_tier: DbModelTier;
    has_onboarded: boolean;
    email: string | null;
    last_sign_in_at: string | null;
    signed_up_at: string | null;
    deactivated_at: string | null;
  };
  teams: TeamItem[];
};

type TeamOp =
  | { type: "add"; team_id: string; role: string }
  | { type: "remove"; team_id: string };

export type UserPanelProps = {
  userId: string;
  allTeams: { id: string; name: string }[];
  onBack: () => void;
  onSaved?: () => void;
  /** Only superadmins can change account access. */
  callerRole?: string | null;
};

// ─── Constants ─────────────────────────────────────────────────────────────────

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL ?? "";
const SUPABASE_KEY = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "";

const SECTIONS = [
  { id: "profile",  label: "Profile" },
  { id: "teams", label: "Teams" },
  { id: "agents", label: "Agents" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));
};

const initials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).map(p => p[0]?.toUpperCase() ?? "").join("").slice(0, 2) || "?";

const roleDescription = (role: UserEditRole) => {
  if (role === "superadmin") return "Full access to all settings, users, and system configuration.";
  if (role === "admin") return "Can manage users, view analytics, and configure team settings.";
  return "Standard access. Can use the platform within their assigned teams.";
};

const formatTeamMembershipRole = (role: string) => {
  if (role === "admin" || role === "superadmin") return "Admin";
  if (role === "team_owner" || role === "owner") return "Owner";
  return "Member";
};

async function authedFetch(url: string, token: string) {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_KEY },
  });
  const json = await res.json();
  if (!res.ok || json.error) throw new Error(json.error ?? "Request failed");
  return json;
}

async function authedPost(url: string, token: string, body: unknown) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_KEY,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok || json.error) throw new Error(json.error ?? "Request failed");
  return json;
}

// ─── Read-only field ───────────────────────────────────────────────────────────

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function UserPanel({ userId, allTeams, onBack, onSaved, callerRole }: UserPanelProps) {
  const session = useSession();
  const [detail, setDetail]   = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]       = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [activeSection, setActiveSection] = useState<SectionId>("profile");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  // Editable fields
  const [displayName, setDisplayName] = useState("");
  const [role, setRole]               = useState<UserEditRole>("user");
  const [tier, setTier]               = useState<DbModelTier>("standard");

  // Team membership edits
  const [memberships, setMemberships]   = useState<TeamItem[]>([]);
  const [teamOps, setTeamOps]           = useState<TeamOp[]>([]);;
  const [addTeamId, setAddTeamId]             = useState("");
  const [addTeamRole, setAddTeamRole]         = useState<TeamMembershipRole>("user");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? "";
      const data = await authedFetch(
        `${SUPABASE_URL}/functions/v1/get-user-detail?user_id=${encodeURIComponent(userId)}`,
        token
      ) as UserDetail;
      setDetail(data);
      setDisplayName(data.profile.display_name ?? "");
      setRole(
        data.profile.role === "superadmin" ? "superadmin"
        : data.profile.role === "admin" ? "admin"
        : "user"
      );
      setTier(data.profile.model_tier ?? "standard");
      setMemberships((data.teams ?? []).filter(t => t.relationship === "member"));
      setTeamOps([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const handleAddTeam = () => {
    if (!addTeamId) return;
      const team = allTeams.find(t => t.id === addTeamId);
    if (!team) return;
    setMemberships(prev => [
      ...prev,
      { id: team.id, name: team.name, is_home: false, relationship: "member", role: addTeamRole, joined_at: new Date().toISOString() },
    ]);
    setTeamOps(prev => [
      ...prev.filter(op => op.team_id !== addTeamId),
      { type: "add", team_id: addTeamId, role: addTeamRole },
    ]);
    setAddTeamId("");
    setAddTeamRole("user");
  };

  const handleRemoveTeam = (teamId: string) => {
    setMemberships(prev => prev.filter(m => m.id !== teamId));
    setTeamOps(prev => [
      ...prev.filter(op => op.team_id !== teamId),
      { type: "remove", team_id: teamId },
    ]);
  };

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? "";
      const url = `${SUPABASE_URL}/functions/v1/update-user`;

      const currentRole: UserEditRole =
        detail.profile.role === "superadmin" ? "superadmin"
        : detail.profile.role === "admin" ? "admin"
        : "user";

      const hasProfileChange =
        displayName.trim() !== (detail.profile.display_name ?? "").trim() ||
        role               !== currentRole ||
        tier               !== detail.profile.model_tier;

      if (hasProfileChange) {
        await authedPost(url, token, { user_id: userId, display_name: displayName, role, model_tier: tier });
      }
      for (const op of teamOps) {
        await authedPost(url, token, { user_id: userId, team_action: op });
      }

      // Update detail in-place so hasChanges becomes false without a re-fetch
      // (a re-fetch would reset saveStatus to "idle" before the button can show "Saved")
      setDetail(prev => prev ? {
        ...prev,
        profile: { ...prev.profile, display_name: displayName, role, model_tier: tier },
        teams: ([
          ...memberships.map(m => ({ ...m, relationship: "member" as "member" | "owner" })),
          ...(prev.teams ?? []).filter(t => t.relationship === "owner"),
        ] as TeamItem[]),
      } : prev);
      setTeamOps([]);
      setSaveStatus("saved");
      onSaved?.();
      toast.success("User updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background">
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background">
        <p className="text-sm text-destructive">{error ?? "No data."}</p>
      </div>
    );
  }

  const { profile, teams } = detail;
  const name = displayName.trim() || profile.username?.trim() || profile.email?.split("@")[0] || "Unknown";

  const originalRole: UserEditRole =
    profile.role === "superadmin" ? "superadmin"
    : profile.role === "admin" ? "admin"
    : "user";
  const hasChanges =
    displayName.trim() !== (profile.display_name ?? "").trim() ||
    role !== originalRole ||
    tier !== profile.model_tier ||
    teamOps.length > 0;

  const memberIds       = new Set(memberships.map(m => m.id));
  const ownedIds        = new Set(teams.filter(t => t.relationship === "owner").map(t => t.id));
  const availableTeams  = allTeams.filter(t => !memberIds.has(t.id) && !ownedIds.has(t.id));
  const ownedTeams      = teams.filter(t => t.relationship === "owner");
  const canChangeAccess = callerRole === "superadmin" && !!session && session.user.id !== userId;

  const sidebarNav = (
    <nav className="flex-1 space-y-0.5">
      {SECTIONS.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => { setActiveSection(id); setMobileSidebarOpen(false); }}
          className={cn(
            "flex items-center justify-between rounded-md px-3 py-2.5 text-left text-sm w-full transition-colors",
            activeSection === id
              ? "bg-accent text-accent-foreground font-medium"
              : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
          )}
        >
          {label}
          {activeSection === id && <ChevronRightIcon className="size-3.5 shrink-0" />}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent className="w-52 p-0" side="left">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Section links for this page.</SheetDescription>
          <div className="flex h-full flex-col p-3">{sidebarNav}</div>
        </SheetContent>
      </Sheet>

      <div className="absolute right-0 top-0 z-10 flex items-center gap-0.5 p-2 sm:hidden">
        <button
          aria-label={mobileSidebarOpen ? "Close navigation" : "Open navigation"}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          onClick={() => setMobileSidebarOpen((o) => !o)}
          type="button"
        >
          {mobileSidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
      </div>

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <PageHeader
        onBack={onBack}
        backLabel="All users"
        showBack
        title={
          <span className="flex items-center gap-2">
            <Avatar className="size-7 shrink-0">
              <AvatarFallback className="bg-foreground text-background text-xs font-semibold">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            {name}
          </span>
        }
        description={undefined}
        actions={
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void load()}
              disabled={saving}
            >
              Reset
            </Button>
            <SaveButton
              isSaving={saving}
              hasChanges={hasChanges}
              savedStatus={saveStatus}
              onSave={() => void handleSave()}
            />
          </div>
        }
      />

      {/* ── Body ───────────────────────────────────────────────────────────────── */}
      <PanelLayout sidebar={sidebarNav} sidebarClassName="hidden sm:flex">
        <div>

            {/* ── Profile ────────────────────────────────────────────────────── */}
            {activeSection === "profile" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Profile</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Basic information about this user.</p>
                  <Separator className="mt-4" />
                </div>

                <div className="flex items-center gap-4">
                  <Avatar className="size-14 shrink-0">
                    <AvatarFallback className="bg-foreground text-background text-lg font-semibold">
                      {initials(name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-base font-semibold text-foreground">{name}</p>
                      {profile.has_onboarded
                        ? <BadgeCheckIcon className="size-4 shrink-0 text-primary" />
                        : <CircleXIcon className="size-4 shrink-0 text-muted-foreground" />}
                    </div>
                    {profile.email && (
                      <p className="text-sm text-muted-foreground">{profile.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="display-name">Display name</Label>
                    <Input
                      id="display-name"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      placeholder="Display name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input value={profile.username ?? ""} disabled className="text-muted-foreground" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <ReadField label="Email"       value={profile.email ?? "—"} />
                  <ReadField label="Team"        value={profile.team ?? "—"} />
                  <ReadField label="Signed up"   value={fmt(profile.signed_up_at)} />
                  <ReadField label="Last sign in" value={fmt(profile.last_sign_in_at)} />
                </div>

                <Separator />

                <div className="space-y-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Permissions</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">Control what this user can access.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={role} onValueChange={v => setRole(v as UserEditRole)}>
                      <SelectTrigger id="role" className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="superadmin">Superadmin</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{roleDescription(role)}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tier">Tier</Label>
                    <Select value={tier} onValueChange={v => setTier(v as DbModelTier)}>
                      <SelectTrigger id="tier" className="w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIER_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Controls which AI models this user can access.</p>
                  </div>

                </div>

                {canChangeAccess && (
                  <>
                    <Separator />
                    <section className="space-y-3">
                      <h3 className="text-sm font-medium">Account access</h3>
                      <Badge variant={profile.deactivated_at ? "outline" : "secondary"}>
                        {profile.deactivated_at ? "Inactive" : "Active"}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        Deactivation blocks access without deleting agents, chats or history.
                        Hand over shared agents in the Agents section.
                      </p>
                      <Button type="button" size="sm" variant={profile.deactivated_at ? "outline" : "destructive"} onClick={() => setAccessOpen(true)}>
                        {profile.deactivated_at ? "Reactivate account" : "Deactivate account"}
                      </Button>
                    </section>
                  </>
                )}
              </div>
            )}

            {/* ── Teams ─────────────────────────────────────────────────────── */}
            {activeSection === "teams" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Teams</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Teams this user belongs to.</p>
                  <Separator className="mt-4" />
                </div>

                {ownedTeams.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Owned</p>
                    <div className="space-y-1.5">
                      {ownedTeams.map(t => (
                        <div key={t.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{t.name}</p>
                            {t.is_home && <p className="text-xs text-muted-foreground">Personal</p>}
                          </div>
                          <Badge variant="outline" className="text-xs">Owner</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Memberships</p>
                  {memberships.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Not a member of any shared teams.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {memberships.map(m => (
                        <div key={m.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{m.name}</p>
                            <p className="text-xs text-muted-foreground">{formatTeamMembershipRole(m.role)}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveTeam(m.id)}
                            aria-label={`Remove from ${m.name}`}
                          >
                            <TrashIcon className="size-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {availableTeams.length > 0 && (
                    <div className="flex gap-2 pt-1">
                      <Select value={addTeamId} onValueChange={setAddTeamId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Add to team…" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTeams.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={addTeamRole} onValueChange={v => setAddTeamRole(v as TeamMembershipRole)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleAddTeam}
                        disabled={!addTeamId}
                        aria-label="Add to team"
                      >
                        <PlusIcon className="size-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Agents ────────────────────────────────────────────────────── */}
            {activeSection === "agents" && (
              <UserAgentsSection userId={userId} userName={name} />
            )}

        </div>
      </PanelLayout>

      {canChangeAccess && (
        <AccountAccessDialog
          open={accessOpen}
          onOpenChange={setAccessOpen}
          userId={userId}
          userName={name}
          confirmText={profile.email?.trim() || name}
          active={!profile.deactivated_at}
          onGoToAgents={() => setActiveSection("agents")}
          onChanged={() => { void load(); onSaved?.(); }}
        />
      )}
    </div>
  );
}
